import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
} from 'react';

const TooltipContext = createContext();

export const useTooltip = () => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltip must be used within a TooltipProvider');
  }
  return context;
};

export const TooltipProvider = ({ children }) => {
  const [openTooltip, setOpenTooltip] = useState(null);
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const tooltipTimeoutRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  // اضافه کردن event listener برای بستن tooltip ها هنگام کلیک خارج از آن‌ها
  useEffect(() => {
    const handleClickOutside = (event) => {
      // اگر tooltip باز است و کلیک خارج از آن انجام شده، tooltip را ببند
      if (openTooltip) {
        closeTooltipHandler();
      }
    };

    const handleMouseLeave = () => {
      closeTooltipHandler();
      clearHoveredIconHandler();
    };

    // اضافه کردن event listener برای mouse movement
    const handleMouseMove = () => {
      // اگر tooltip باز است و mouse حرکت کرده، timeout را reset کن
      if (openTooltip && tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = setTimeout(() => {
          setOpenTooltip(null);
        }, 5000);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mousemove', handleMouseMove);
      cleanup();
    };
  }, [openTooltip]);

  const openTooltipHandler = (tooltipId) => {
    // پاک کردن timeout های قبلی اگر وجود دارند
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    // اگر tooltip دیگری باز است، آن را فوراً ببند و tooltip جدید را باز کن
    if (openTooltip && openTooltip !== tooltipId) {
      setOpenTooltip(null);
      // بدون تاخیر، tooltip جدید را باز کن
      setOpenTooltip(tooltipId);
      // تنظیم timeout برای بستن خودکار tooltip بعد از 5 ثانیه
      tooltipTimeoutRef.current = setTimeout(() => {
        setOpenTooltip(null);
      }, 5000);
    } else if (openTooltip === tooltipId) {
      // اگر همان tooltip است، آن را ببند
      setOpenTooltip(null);
    } else {
      // اگر هیچ tooltip باز نیست، tooltip جدید را باز کن
      setOpenTooltip(tooltipId);
      // تنظیم timeout برای بستن خودکار tooltip بعد از 5 ثانیه
      tooltipTimeoutRef.current = setTimeout(() => {
        setOpenTooltip(null);
      }, 5000);
    }
  };

  const closeTooltipHandler = () => {
    // پاک کردن timeout ها اگر وجود دارند
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    setOpenTooltip(null);
  };

  const setHoveredIconHandler = (iconId) => {
    setHoveredIcon(iconId);
  };

  const clearHoveredIconHandler = () => {
    setHoveredIcon(null);
  };

  const cleanup = () => {
    // پاک کردن timeout ها هنگام unmount
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  };

  const value = {
    openTooltip,
    hoveredIcon,
    openTooltipHandler,
    closeTooltipHandler,
    setHoveredIconHandler,
    clearHoveredIconHandler,
    cleanup,
  };

  return (
    <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>
  );
};
